/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Grid, typography2 } from '@coursera/cds-core';

import TestimonialItem from 'bundles/cds-labs/components/Modules/TestimonialsModule/TestimonialItem';
import type { CareerRolesFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import { getProductSlugs } from 'bundles/front-page/components/modules/career-roles/CareerRolePanel';
import ProductCards from 'bundles/front-page/components/modules/career-roles/ProductCards';
import Modal from 'bundles/unified-description-page-common/components/shared/modals/Modal';
import type { ModalProps } from 'bundles/unified-description-page-common/components/shared/modals/Modal';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

const styles = {
  modal: css`
    h2 {
      font-size: var(--cds-font-size-300);
      line-height: var(--cds-line-height-400);
    }

    .cds-ProductCard-list {
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
      height: initial;
    }

    .cds-CommonCard-bodyContent {
      /* limit skills to 2 lines */
      p {
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
      }

      b {
        font-weight: var(--cds-font-weight-400);
        color: var(--cds-color-neutral-primary-weak);
      }
    }
  `,
  careerRoleTestimonial: css`
    > div {
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }

    span {
      ${typography2.bodySecondary}
    }
  `,
};

type CareerRoleRecommendationProps = ModalProps & {
  role: CareerRolesFieldsFragment;
};

const CareerRoleRecommendationsModal = ({ role, ...modalProps }: CareerRoleRecommendationProps) => {
  const productSlugs = getProductSlugs(role);
  const showProductCards = productSlugs?.length;

  if (!showProductCards || !role.title) {
    return null;
  }

  const testimonialName = role.testimonial?.authorName;
  const testimonialQuote = role.testimonial?.text;
  const testimonialImg = role.testimonial?.image?.url;

  const hasTestimonial = role.testimonial && testimonialName && testimonialQuote && testimonialImg;

  return (
    <Modal
      {...modalProps}
      hideActions
      header={_t('Recommended Professional Certificates for #{roleTitle}', { roleTitle: role.title })}
      width="large"
      closeModalTrackingName="close_career_role_recs_modal"
      primaryButtonTrackingName="open_career_role_recs_modal"
      css={styles.modal}
    >
      <Grid container>
        <ProductCards slugs={productSlugs} roleName={role.title} />
        {hasTestimonial && (
          <Grid item xs={12} css={styles.careerRoleTestimonial}>
            <TestimonialItem name={testimonialName} quote={testimonialQuote} imageSrc={testimonialImg} />
          </Grid>
        )}
      </Grid>
    </Modal>
  );
};

export default CareerRoleRecommendationsModal;
